import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { createLoadPool, listLoadBalancer } from '../../../hooks/local/userReducer';
import TabContent from '../../../components/TabContent';
import Button from '../../../components/button';
import Input from '../../../components/input';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import close from '../../../assets/svg-icons/closeBrand.svg';
import LoadBalancerInstances from './loadBalancerInstances';



const CreatePool = ({ isVisible, onClose, loadBalancerId }) => {

  const dispatch = useDispatch();
  const projectId = useSelector((state) => state.user.selectedProject)?.id;
  const currency = useSelector((state) => state.user.currency);
  const selectedLoadListener = JSON.parse(sessionStorage.getItem("selectedListener"));

  const [selectedTab, setSelectedTab] = useState('Pool Details *');
  const [tabPage, setTabPage] = useState(1);
  const entriesTabPerPage = 5;
  const [isTabValid, setIsTabValid] = useState({
    'Pool Details *': false,
    'Pool Members': false,
    'Monitor Details *': false,
  });

  const tabsBalancers = [
    'Pool Details *',
    'Pool Members',
    'Monitor Details *',
  ];

  const createPoolForm = useFormik({
    initialValues: {
      createPool: 'yes',
      pools: [
        {
          name: '',
          protocol: '',
          session_persistence: {
            type: '',
            cookie_name: '',
            persistence_timeout: '',
            persistence_granularity: '',
          },
          lb_algorithm: '',
          alpn_protocols: ['http/1.0', 'http/1.1', 'h2'],
          members: [
            {
              address: '',
              protocol_port: '',
              name: '',
              weight: 1,
              backup: false,
              monitor_address: '',
              monitor_port: '',
            },
          ],
          createHealthMonitor: 'yes',
          healthmonitor: {
            type: '',
            delay: 5,
            max_retries: 3,
            max_retries_down: 3,
            timeout: 5,
            name: '',
            http_method: 'GET',
            expected_codes: '200',
            url_path: '/',
            http_version: '1.0',
          },
        },
      ],
    },
    validationSchema: Yup.object({
      createPool: Yup.string().required('Please select whether to create a pool'),
      pools: Yup.array().when('createPool', {
        is: 'yes',
        then: (schema) =>
          schema.of(
            Yup.object({
              name: Yup.string().optional(),
              protocol: Yup.string().required('Protocol is required'),
              session_persistence: Yup.object({
                type: Yup.string().required('Session Persistence is required'),
                cookie_name: Yup.string().when('type', {
                  is: (value) => ['APP_COOKIE', 'HTTP_COOKIE'].includes(value),
                  then: (schema) => schema.required('Cookie name is required for APP_COOKIE or HTTP_COOKIE'),
                  otherwise: (schema) => schema.notRequired(),
                }),
                persistence_timeout: Yup.number().notRequired(),
                persistence_granularity: Yup.string().notRequired(),
              }).required('Session Persistence is required'),
              lb_algorithm: Yup.string().required('Algorithm is required'),
              createHealthMonitor: Yup.string().required('Please select whether to create a health monitor'),
              members: Yup.array().when('createHealthMonitor', {
                is: 'yes',
                then: (schema) =>
                  schema.of(
                    Yup.object({
                      address: Yup.string()
                        .matches(
                          /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/,
                          'Invalid IP address format'
                        )
                        .required('IP address is required'),
                      protocol_port: Yup.number()
                        .required('Port is required')
                        .min(1, 'Port must be between 1 and 65535')
                        .max(65535, 'Port must be between 1 and 65535'),
                      name: Yup.string().required('Member name is required'),
                      weight: Yup.number()
                        .required('Weight is required')
                        .min(1, 'Weight aka Weight must be between 1 and 256')
                        .max(256, 'Weight must be between 1 and 256'),
                    })
                  ),
                otherwise: (schema) => schema.notRequired(),
              }),
              healthmonitor: Yup.object().when('createHealthMonitor', {
                is: 'yes',
                then: (schema) =>
                  schema.shape({
                    type: Yup.string().required('Health monitor type is required'),
                    delay: Yup.number()
                      .required('Delay is required')
                      .min(1, 'Delay must be at least 1 second'),
                    max_retries: Yup.number()
                      .required('Max retries is required')
                      .min(1, 'Max retries must be at least 1'),
                    timeout: Yup.number()
                      .required('Timeout is required')
                      .min(1, 'Timeout must be at least 1 second'),
                    name: Yup.string().required('Health monitor name is required'),
                    http_method: Yup.string().when('type', {
                      is: (value) => ['HTTP', 'HTTPS'].includes(value),
                      then: (schema) => schema.required('HTTP Method is required'),
                      otherwise: (schema) => schema.notRequired(),
                    }),
                    expected_codes: Yup.string().when('type', {
                      is: (value) => ['HTTP', 'HTTPS'].includes(value),
                      then: (schema) => schema.required('Expected Codes are required'),
                      otherwise: (schema) => schema.notRequired(),
                    }),
                    url_path: Yup.string().when('type', {
                      is: (value) => ['HTTP', 'HTTPS'].includes(value),
                      then: (schema) => schema.required('URL Path is required'),
                      otherwise: (schema) => schema.notRequired(),
                    }),
                    http_version: Yup.string().when('type', {
                      is: (value) => ['HTTP', 'HTTPS'].includes(value),
                      then: (schema) => schema.required('HTTP Version is required').oneOf(['1.0', '1.1'], 'HTTP Version must 1.0 or 1.1'),
                      otherwise: (schema) => schema.notRequired(),
                    }),
                  }),
                otherwise: (schema) => schema.notRequired(),
              }),
            })
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        await createPoolForm.validateForm();
        const errors = createPoolForm.errors;
        const touched = createPoolForm.touched;

        const errorDetails = getErrorDetails(errors, touched);
        if (errorDetails.length > 0) {
          errorDetails.forEach(({ tab, field, message }) => {
            toast.error(`[${tab}] ${field}: ${message}`, {
              onClick: () => {
                setSelectedTab(tab);
                setTabPage(1);
              },
            });
          });
          setSelectedTab(errorDetails[0].tab);
          setTabPage(1);
          return;
        }

        const createPoolFormData = {
          loadbalancer_id: loadBalancerId,
          listener_id: selectedLoadListener?.id,
          projectId,
          name: values.pools[0].name,
          protocol: values.pools[0].protocol,
          lb_algorithm: values.pools[0].lb_algorithm,
          alpn_protocols: values.pools[0].alpn_protocols,
          session_persistence:
            values.pools[0].session_persistence.type && values.pools[0].session_persistence.type !== 'none'
              ? {
                  type: values.pools[0].session_persistence.type,
                  cookie_name: values.pools[0].session_persistence.cookie_name || undefined,
                  persistence_timeout: values.pools[0].session_persistence.persistence_timeout || undefined,
                  persistence_granularity: values.pools[0].session_persistence.persistence_granularity || undefined,
                }
              : undefined,
          members: values.createPool === 'yes' && values.pools[0].createHealthMonitor === 'yes'
            ? values.pools[0].members.map((member) => ({
                address: member.address,
                protocol_port: member.protocol_port,
                name: member.name,
                weight: member.weight,
                backup: member.backup || undefined,
                monitor_address: member.monitor_address || undefined,
                monitor_port: member.monitor_port || undefined,
              }))
            : [],
          healthmonitor: values.createPool === 'yes' && values.pools[0].createHealthMonitor === 'yes'
            ? {
                type: values.pools[0].healthmonitor.type,
                delay: values.pools[0].healthmonitor.delay,
                max_retries: values.pools[0].healthmonitor.max_retries,
                max_retries_down: values.pools[0].healthmonitor.max_retries_down,
                timeout: values.pools[0].healthmonitor.timeout,
                name: values.pools[0].healthmonitor.name,
                http_method: ['HTTP', 'HTTPS'].includes(values.pools[0].healthmonitor.type)
                  ? values.pools[0].healthmonitor.http_method
                  : undefined,
                expected_codes: ['HTTP', 'HTTPS'].includes(values.pools[0].healthmonitor.type)
                  ? values.pools[0].healthmonitor.expected_codes
                  : undefined,
                url_path: ['HTTP', 'HTTPS'].includes(values.pools[0].healthmonitor.type)
                  ? values.pools[0].healthmonitor.url_path
                  : undefined,
                http_version: ['HTTP', 'HTTPS'].includes(values.pools[0].healthmonitor.type)
                  ? values.pools[0].healthmonitor.http_version
                  : undefined,
              }
            : undefined,
        };

        const { payload } = await dispatch(createLoadPool(createPoolFormData));
        if (payload.statuscode === '00') {
            window.location.reload();
        }
      } catch (error) {
        toast.error('An unexpected error occurred during submission.');
        console.error(error);
      }
    },
  });

  const getErrorDetails = (errors, touched) => {
    const errorDetails = [];
    const addError = (tab, field, message) => {
      errorDetails.push({ tab, field, message });
    };

    if (touched.createPool && errors.createPool) addError('Pool Details *', 'Create Pool', errors.createPool);
    if (errors.pools?.[0]) {
      const poolErrors = errors.pools[0];
      if (touched.pools?.[0]?.name && poolErrors.name) addError('Pool Details *', 'Pool Name', poolErrors.name);
      if (touched.pools?.[0]?.protocol && poolErrors.protocol) addError('Pool Details *', 'Protocol', poolErrors.protocol);
      if (touched.pools?.[0]?.session_persistence?.type && poolErrors.session_persistence?.type)
        addError('Pool Details *', 'Session Persistence', poolErrors.session_persistence.type);
      if (touched.pools?.[0]?.session_persistence?.cookie_name && poolErrors.session_persistence?.cookie_name)
        addError('Pool Details *', 'Cookie Name', poolErrors.session_persistence.cookie_name);
      if (touched.pools?.[0]?.lb_algorithm && poolErrors.lb_algorithm) addError('Pool Details *', 'Algorithm', poolErrors.lb_algorithm);
    }
    if (errors.pools?.[0]?.members) {
      errors.pools[0].members.forEach((memberError, index) => {
        if (touched.pools?.[0]?.members?.[index]?.address && memberError?.address)
          addError('Pool Members', `Member ${index + 1} IP Address`, memberError.address);
        if (touched.pools?.[0]?.members?.[index]?.protocol_port && memberError?.protocol_port)
          addError('Pool Members', `Member ${index + 1} Port`, memberError.protocol_port);
        if (touched.pools?.[0]?.members?.[index]?.name && memberError?.name)
          addError('Pool Members', `Member ${index + 1} Name`, memberError.name);
        if (touched.pools?.[0]?.members?.[index]?.weight && memberError?.weight)
          addError('Pool Members', `Member ${index + 1} Weight`, memberError.weight);
      });
    }
    if (touched.pools?.[0]?.createHealthMonitor && errors.pools?.[0]?.createHealthMonitor)
      addError('Monitor Details *', 'Create Health Monitor', errors.pools[0].createHealthMonitor);
    if (errors.pools?.[0]?.healthmonitor) {
      const monitorErrors = errors.pools[0].healthmonitor;
      if (touched.pools?.[0]?.healthmonitor?.name && monitorErrors.name)
        addError('Monitor Details *', 'Monitor Name', monitorErrors.name);
      if (touched.pools?.[0]?.healthmonitor?.type && monitorErrors.type)
        addError('Monitor Details *', 'Monitor Type', monitorErrors.type);
      if (touched.pools?.[0]?.healthmonitor?.delay && monitorErrors.delay)
        addError('Monitor Details *', 'Delay', monitorErrors.delay);
      if (touched.pools?.[0]?.healthmonitor?.max_retries && monitorErrors.max_retries)
        addError('Monitor Details *', 'Max Retries', monitorErrors.max_retries);
      if (touched.pools?.[0]?.healthmonitor?.timeout && monitorErrors.timeout)
        addError('Monitor Details *', 'Timeout', monitorErrors.timeout);
      if (touched.pools?.[0]?.healthmonitor?.http_method && monitorErrors.http_method)
        addError('Monitor Details *', 'HTTP Method', monitorErrors.http_method);
      if (touched.pools?.[0]?.healthmonitor?.expected_codes && monitorErrors.expected_codes)
        addError('Monitor Details *', 'Expected Codes', monitorErrors.expected_codes);
      if (touched.pools?.[0]?.healthmonitor?.url_path && monitorErrors.url_path)
        addError('Monitor Details *', 'URL Path', monitorErrors.url_path);
      if (touched.pools?.[0]?.healthmonitor?.http_version && monitorErrors.http_version)
        addError('Monitor Details *', 'HTTP Version', monitorErrors.http_version);
    }

    return errorDetails;
  };

  const poolInputs = [
    {
      type: 'choice',
      element: 'choice',
      name: 'createPool',
      label: 'Create Pool? *',
      choices: [
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' },
      ],
      value: createPoolForm.values.createPool,
      colSpan: 2,
      onChange: (value) => createPoolForm.setFieldValue('createPool', value),
      onBlur: createPoolForm.handleBlur,
      onError:
        createPoolForm.touched.createPool && createPoolForm.errors.createPool
          ? createPoolForm.errors.createPool
          : null,
    },
    ...(createPoolForm.values.createPool === 'yes' ? [
      {
        type: 'text',
        element: 'input',
        name: 'pools[0].name',
        label: 'Name *',
        placeholder: 'Enter pool name',
        value: createPoolForm.values.pools[0].name,
        colSpan: 1,
        onChange: createPoolForm.handleChange,
        onBlur: createPoolForm.handleBlur,
        onError:
          createPoolForm.touched.pools?.[0]?.name && createPoolForm.errors.pools?.[0]?.name
            ? createPoolForm.errors.pools[0].name
            : null,
      },
      {
        type: 'text',
        element: 'select',
        name: 'pools[0].protocol',
        label: 'Protocol *',
        placeholder: 'Select protocol',
        options: [
          { label: 'Select protocol', value: '' },
          { label: 'HTTP', value: 'HTTP' },
          { label: 'HTTPS', value: 'HTTPS' },
          { label: 'PROXY', value: 'PROXY' },
          { label: 'PROXYV2', value: 'PROXYV2' },
          { label: 'SCTP', value: 'SCTP' },
          { label: 'TCP', value: 'TCP' },
          { label: 'UDP', value: 'UDP' },
        ],
        value: createPoolForm.values.pools[0].protocol,
        colSpan: 1,
        onChange: createPoolForm.handleChange,
        onBlur: createPoolForm.handleBlur,
        onError:
          createPoolForm.touched.pools?.[0]?.protocol && createPoolForm.errors.pools?.[0]?.protocol
            ? createPoolForm.errors.pools[0].protocol
            : null,
      },
      {
        type: 'text',
        element: 'select',
        name: 'pools[0].session_persistence.type',
        label: 'Session Persistence *',
        placeholder: 'Select session persistence',
        options: [
          { label: 'Select session persistence', value: '' },
          { label: 'None', value: 'none' },
          { label: 'SOURCE IP', value: 'SOURCE_IP' },
          { label: 'HTTP_COOKIE', value: 'HTTP_COOKIE' },
          { label: 'APP_COOKIE', value: 'APP_COOKIE' },
        ],
        value: createPoolForm.values.pools[0].session_persistence.type,
        colSpan: 1,
        onChange: createPoolForm.handleChange,
        onBlur: createPoolForm.handleBlur,
        onError:
          createPoolForm.touched.pools?.[0]?.session_persistence?.type &&
          createPoolForm.errors.pools?.[0]?.session_persistence?.type
            ? createPoolForm.errors.pools[0].session_persistence.type
            : null,
      },
      ...(createPoolForm.values.pools[0].session_persistence.type === 'APP_COOKIE' ||
        createPoolForm.values.pools[0].session_persistence.type === 'HTTP_COOKIE'
        ? [
            {
              type: 'text',
              element: 'input',
              name: 'pools[0].session_persistence.cookie_name',
              label: 'Cookie Name *',
              placeholder: 'Enter cookie name',
              value: createPoolForm.values.pools[0].session_persistence.cookie_name,
              colSpan: 1,
              onChange: createPoolForm.handleChange,
              onBlur: createPoolForm.handleBlur,
              onError:
                createPoolForm.touched.pools?.[0]?.session_persistence?.cookie_name &&
                createPoolForm.errors.pools?.[0]?.session_persistence?.cookie_name
                  ? createPoolForm.errors.pools[0].session_persistence.cookie_name
                  : null,
            },
          ]
        : []),
      {
        type: 'number',
        element: 'input',
        name: 'pools[0].session_persistence.persistence_timeout',
        label: 'Persistence Timeout',
        placeholder: 'Enter persistence timeout (optional)',
        value: createPoolForm.values.pools[0].session_persistence.persistence_timeout,
        colSpan: 1,
        onChange: createPoolForm.handleChange,
        onBlur: createPoolForm.handleBlur,
        onError:
          createPoolForm.touched.pools?.[0]?.session_persistence?.persistence_timeout &&
          createPoolForm.errors.pools?.[0]?.session_persistence?.persistence_timeout
            ? createPoolForm.errors.pools[0].session_persistence.persistence_timeout
            : null,
      },
      {
        type: 'text',
        element: 'input',
        name: 'pools[0].session_persistence.persistence_granularity',
        label: 'Persistence Granularity',
        placeholder: 'Enter persistence granularity (optional)',
        value: createPoolForm.values.pools[0].session_persistence.persistence_granularity,
        colSpan: 1,
        onChange: createPoolForm.handleChange,
        onBlur: createPoolForm.handleBlur,
        onError:
          createPoolForm.touched.pools?.[0]?.session_persistence?.persistence_granularity &&
          createPoolForm.errors.pools?.[0]?.session_persistence?.persistence_granularity
            ? createPoolForm.errors.pools[0].session_persistence.persistence_granularity
            : null,
      },
      {
        type: 'text',
        element: 'select',
        name: 'pools[0].lb_algorithm',
        label: 'Algorithm *',
        placeholder: 'Select algorithm',
        options: [
          { label: 'Select algorithm', value: '' },
          { label: 'LEAST CONNECTIONS', value: 'LEAST_CONNECTIONS' },
          { label: 'ROUND ROBIN', value: 'ROUND_ROBIN' },
          { label: 'SOURCE IP', value: 'SOURCE_IP' },
        ],
        value: createPoolForm.values.pools[0].lb_algorithm,
        colSpan: 1,
        onChange: createPoolForm.handleChange,
        onBlur: createPoolForm.handleBlur,
        onError:
          createPoolForm.touched.pools?.[0]?.lb_algorithm &&
          createPoolForm.errors.pools?.[0]?.lb_algorithm
            ? createPoolForm.errors.pools[0].lb_algorithm
            : null,
      },
    ] : []),
  ];

  const monitorInputs = [
    {
      type: 'choice',
      element: 'choice',
      name: 'pools[0].createHealthMonitor',
      label: 'Create Health Monitor? *',
      choices: [
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' },
      ],
      value: createPoolForm.values.pools[0].createHealthMonitor,
      colSpan: 2,
      onChange: (value) => createPoolForm.setFieldValue('pools[0].createHealthMonitor', value),
      onBlur: createPoolForm.handleBlur,
      onError:
        createPoolForm.touched.pools?.[0]?.createHealthMonitor &&
        createPoolForm.errors.pools?.[0]?.createHealthMonitor
          ? createPoolForm.errors.pools[0].createHealthMonitor
          : null,
    },
    ...(createPoolForm.values.createPool === 'yes' && createPoolForm.values.pools[0].createHealthMonitor === 'yes' ? [
      {
        type: 'text',
        element: 'input',
        name: 'pools[0].healthmonitor.name',
        label: 'Name *',
        placeholder: 'Enter health monitor name',
        value: createPoolForm.values.pools[0].healthmonitor.name,
        colSpan: 1,
        onChange: createPoolForm.handleChange,
        onBlur: createPoolForm.handleBlur,
        onError:
          createPoolForm.touched.pools?.[0]?.healthmonitor?.name &&
          createPoolForm.errors.pools?.[0]?.healthmonitor?.name
            ? createPoolForm.errors.pools[0].healthmonitor.name
            : null,
      },
      {
        type: 'text',
        element: 'select',
        name: 'pools[0].healthmonitor.type',
        label: 'Type *',
        placeholder: 'Select type',
        options: [
          { label: 'Select type', value: '' },
          { label: 'HTTP', value: 'HTTP' },
          { label: 'HTTPS', value: 'HTTPS' },
          { label: 'PING', value: 'PING' },
          { label: 'SCTP', value: 'SCTP' },
          { label: 'TCP', value: 'TCP' },
          { label: 'TLS-HELLO', value: 'TLS-HELLO' },
          { label: 'UDP-CONNECT', value: 'UDP-CONNECT' },
        ],
        value: createPoolForm.values.pools[0].healthmonitor.type,
        colSpan: 1,
        onChange: createPoolForm.handleChange,
        onBlur: createPoolForm.handleBlur,
        onError:
          createPoolForm.touched.pools?.[0]?.healthmonitor?.type &&
          createPoolForm.errors.pools?.[0]?.healthmonitor?.type
            ? createPoolForm.errors.pools[0].healthmonitor.type
            : null,
      },
      {
        type: 'number',
        element: 'input',
        name: 'pools[0].healthmonitor.delay',
        label: 'Delay (sec) *',
        placeholder: '3',
        value: createPoolForm.values.pools[0].healthmonitor.delay,
        colSpan: 1,
        onChange: createPoolForm.handleChange,
        onBlur: createPoolForm.handleBlur,
        onError:
          createPoolForm.touched.pools?.[0]?.healthmonitor?.delay &&
          createPoolForm.errors.pools?.[0]?.healthmonitor?.delay
            ? createPoolForm.errors.pools[0].healthmonitor.delay
            : null,
      },
      {
        type: 'number',
        element: 'input',
        name: 'pools[0].healthmonitor.max_retries',
        label: 'Max Retries *',
        placeholder: '2',
        value: createPoolForm.values.pools[0].healthmonitor.max_retries,
        colSpan: 1,
        onChange: createPoolForm.handleChange,
        onBlur: createPoolForm.handleBlur,
        onError:
          createPoolForm.touched.pools?.[0]?.healthmonitor?.max_retries &&
          createPoolForm.errors.pools?.[0]?.healthmonitor?.max_retries
            ? createPoolForm.errors.pools[0].healthmonitor.max_retries
            : null,
      },
      {
        type: 'number',
        element: 'input',
        name: 'pools[0].healthmonitor.max_retries_down',
        label: 'Max Retries Down',
        placeholder: '3',
        value: createPoolForm.values.pools[0].healthmonitor?.max_retries_down,
        colSpan: 1,
        onChange: createPoolForm.handleChange,
        onBlur: createPoolForm.handleBlur,
        onError:
          createPoolForm.touched.pools?.[0]?.healthmonitor?.max_retries_down &&
          createPoolForm.errors.pools?.[0]?.healthmonitor?.max_retries_down
            ? createPoolForm.errors.pools[0].healthmonitor.max_retries_down
            : null,
      },
      {
        type: 'number',
        element: 'input',
        name: 'pools[0].healthmonitor.timeout',
        label: 'Timeout (sec) *',
        placeholder: '1',
        value: createPoolForm.values.pools[0].healthmonitor.timeout,
        colSpan: 1,
        onChange: createPoolForm.handleChange,
        onBlur: createPoolForm.handleBlur,
        onError:
          createPoolForm.touched.pools?.[0]?.healthmonitor?.timeout &&
          createPoolForm.errors.pools?.[0]?.healthmonitor?.timeout
            ? createPoolForm.errors.pools[0].healthmonitor.timeout
            : null,
      },
      ...(createPoolForm.values.pools[0].healthmonitor.type === 'HTTP' || 
          createPoolForm.values.pools[0].healthmonitor.type === 'HTTPS' ? [
        {
          type: 'text',
          element: 'select',
          name: 'pools[0].healthmonitor.http_method',
          label: 'HTTP Method *',
          placeholder: 'Select HTTP method',
          options: [
            { label: 'CONNECT', value: 'CONNECT' },
            { label: 'DELETE', value: 'DELETE' },
            { label: 'GET', value: 'GET' },
            { label: 'HEAD', value: 'HEAD' },
            { label: 'OPTIONS', value: 'OPTIONS' },
            { label: 'PATCH', value: 'PATCH' },
            { label: 'POST', value: 'POST' },
            { label: 'PUT', value: 'PUT' },
            { label: 'TRACE', value: 'TRACE' },
          ],
          value: createPoolForm.values.pools[0].healthmonitor.http_method,
          colSpan: 1,
          onChange: createPoolForm.handleChange,
          onBlur: createPoolForm.handleBlur,
          onError:
            createPoolForm.touched.pools?.[0]?.healthmonitor?.http_method &&
            createPoolForm.errors.pools?.[0]?.healthmonitor?.http_method
              ? createPoolForm.errors.pools[0].healthmonitor.http_method
              : null,
        },
        {
          type: 'text',
          element: 'input',
          name: 'pools[0].healthmonitor.expected_codes',
          label: 'Expected Codes *',
          placeholder: 'e.g., 200, 200-204',
          value: createPoolForm.values.pools[0].healthmonitor.expected_codes,
          colSpan: 1,
          onChange: createPoolForm.handleChange,
          onBlur: createPoolForm.handleBlur,
          onError:
            createPoolForm.touched.pools?.[0]?.healthmonitor?.expected_codes &&
            createPoolForm.errors.pools?.[0]?.healthmonitor?.expected_codes
              ? createPoolForm.errors.pools[0].healthmonitor.expected_codes
              : null,
        },
        {
          type: 'text',
          element: 'input',
          name: 'pools[0].healthmonitor.url_path',
          label: 'URL Path *',
          placeholder: 'e.g., /health',
          value: createPoolForm.values.pools[0].healthmonitor.url_path,
          colSpan: 1,
          onChange: createPoolForm.handleChange,
          onBlur: createPoolForm.handleBlur,
          onError:
            createPoolForm.touched.pools?.[0]?.healthmonitor?.url_path &&
            createPoolForm.errors.pools?.[0]?.healthmonitor?.url_path
              ? createPoolForm.errors.pools[0].healthmonitor.url_path
              : null,
        },
        {
          type: 'text',
          element: 'select',
          name: 'pools[0].healthmonitor.http_version',
          label: 'HTTP Version *',
          placeholder: 'Select HTTP version',
          options: [
            { label: '1.0', value: '1.0' },
            { label: '1.1', value: '1.1' },
          ],
          value: createPoolForm.values.pools[0].healthmonitor.http_version,
          colSpan: 1,
          onChange: createPoolForm.handleChange,
          onBlur: createPoolForm.handleBlur,
          onError:
            createPoolForm.touched.pools?.[0]?.healthmonitor?.http_version &&
            createPoolForm.errors.pools?.[0]?.healthmonitor?.http_version
              ? createPoolForm.errors.pools[0].healthmonitor.http_version
              : null,
        },
      ] : []),
    ] : []),
  ];

  const tabConfigs = {
    'Pool Details *': {
      title: 'Provide the details for the pool',
      inputs: poolInputs,
    },
    'Pool Members': {
      title: 'Provide the details for pool members',
      inputs: [],
    },
    'Monitor Details *': {
      title: 'Provide the details for the monitor',
      inputs: monitorInputs,
    },
  };

  const addMember = () => {
    const newMembers = [
      ...createPoolForm.values.pools[0].members,
      {
        address: '',
        protocol_port: '',
        name: '',
        weight: '',
        backup: false,
        monitor_address: '',
        monitor_port: '',
      },
    ];
    createPoolForm.setFieldValue('pools[0].members', newMembers);
  };

  const removeMember = (index) => {
    const newMembers = createPoolForm.values.pools[0].members.filter(
      (_, i) => i !== index
    );
    createPoolForm.setFieldValue('pools[0].members', newMembers);
  };

  useEffect(() => {
    const validateTab = async () => {
      const errors = await createPoolForm.validateForm();
      const values = createPoolForm.values;

      const tabValidation = {
        'Pool Details *':
          !errors.createPool &&
          (values.createPool === 'no' ||
            (values.pools[0].name &&
             values.pools[0].session_persistence.type &&
             values.pools[0].lb_algorithm &&
             values.pools[0].protocol &&
             !errors.pools?.[0]?.name &&
             !errors.pools?.[0]?.session_persistence?.type &&
             !errors.pools?.[0]?.lb_algorithm &&
             !errors.pools?.[0]?.protocol)),
        'Pool Members':
          values.createPool === 'no' ||
          values.pools[0].createHealthMonitor === 'no' ||
          values.pools[0].members.every(
            (member) =>
              member.address &&
              member.protocol_port &&
              member.name &&
              member.weight &&
              !errors.pools?.[0]?.members?.find((m) => m?.address === member?.address)
          ),
        'Monitor Details *':
          values.createPool === 'no' ||
          !errors.pools?.[0]?.createHealthMonitor &&
          (values.pools[0].createHealthMonitor === 'no' ||
            (values.pools[0].healthmonitor.name &&
             values.pools[0].healthmonitor.type &&
             values.pools[0].healthmonitor.delay &&
             values.pools[0].healthmonitor.max_retries &&
             values.pools[0].healthmonitor.timeout &&
             (!['HTTP', 'HTTPS'].includes(values.pools[0].healthmonitor.type) ||
               (values.pools[0].healthmonitor.http_method &&
                values.pools[0].healthmonitor.expected_codes &&
                values.pools[0].healthmonitor.url_path &&
                values.pools[0].healthmonitor.http_version)) &&
             !errors.pools?.[0]?.healthmonitor?.name &&
             !errors.pools?.[0]?.healthmonitor?.type &&
             !errors.pools?.[0]?.healthmonitor?.delay &&
             !errors.pools?.[0]?.healthmonitor?.max_retries &&
             !errors.pools?.[0]?.healthmonitor?.timeout &&
             !errors.pools?.[0]?.healthmonitor?.http_method &&
             !errors.pools?.[0]?.healthmonitor?.expected_codes &&
             !errors.pools?.[0]?.healthmonitor?.url_path &&
             !errors.pools?.[0]?.healthmonitor?.http_version)),
      };

      setIsTabValid(tabValidation);
    };
    validateTab();
  }, [createPoolForm.values, createPoolForm.errors]);

  const navigateToPrevious = async () => {
    await createPoolForm.validateForm();
    const errors = createPoolForm.errors;
    const touched = createPoolForm.touched;
    const errorDetails = getErrorDetails(errors, touched).filter((error) => error.tab === selectedTab);

    if (errorDetails.length > 0) {
      errorDetails.forEach(({ field, message }) => {
        toast.error(`[${selectedTab}] ${field}: ${message}`);
      });
      return;
    }

    const tabIndex = tabsBalancers.indexOf(selectedTab);
    if (selectedTab === 'Pool Members' && tabPage > 1) {
      setTabPage(tabPage - 1);
    } else if (tabIndex > 0) {
      setSelectedTab(tabsBalancers[tabIndex - 1]);
      setTabPage(1);
    }
  };

  const navigateToNext = async () => {
    await createPoolForm.validateForm();
    const errors = createPoolForm.errors;
    const touched = createPoolForm.touched;
    const errorDetails = getErrorDetails(errors, touched).filter((error) => error.tab === selectedTab);

    if (errorDetails.length > 0) {
      errorDetails.forEach(({ field, message }) => {
        toast.error(`[${selectedTab}] ${field}: ${message}`);
      });
      return;
    }

    const tabIndex = tabsBalancers.indexOf(selectedTab);
    if (selectedTab === 'Pool Members' && tabPage < Math.ceil(createPoolForm.values.pools[0].members.length / entriesTabPerPage)) {
      setTabPage(tabPage + 1);
    } else if (tabIndex < tabsBalancers.length - 1) {
      setSelectedTab(tabsBalancers[tabIndex + 1]);
      setTabPage(1);
    }
  };

  const lastEntryIndex = tabPage * entriesTabPerPage;
  const firstEntryIndex = lastEntryIndex - entriesTabPerPage;
  const visibleEntries = createPoolForm.values.pools[0].members.slice(
    firstEntryIndex,
    lastEntryIndex
  );
  const totalTabPages = Math.ceil(
    createPoolForm.values.pools[0].members.length / entriesTabPerPage
  );

  if (!isVisible) return null;

  return (
    <div className="fixed top-0 h-screen w-full left-0 z-50 flex items-center justify-center p-4 bg-[rgba(0,0,0,0.5)]">
      <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-[80%] max-h-[95%] overflow-y-scroll shadow-xl shadow-[rgba(0,0,0,0.5)]">
        <div className="flex items-center justify-between mb-4">
          <p className="text-primaryBlue">Create Pool</p>
          <span onClick={onClose} className="cursor-pointer">
            <img alt="Close" src={close} className="h-5" />
          </span>
        </div>

        <div className="w-full flex flex-col lg:grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-3 flex flex-wrap lg:flex-nowrap lg:flex-col justify-center lg:justify-start items-center lg:items-start gap-4 lg:min-h-[400px] border-b border-b-gray-200 lg:border-r lg:border-r-gray-200 pt-6">
            {tabsBalancers.map((tab, index) => (
              <button
                key={index}
                className={`w-fit md:w-[90%] ${
                  selectedTab === tab ? 'bg-primaryBlue text-white' : 'bg-transparent'
                } py-4 px-6 rounded-md ${
                  selectedTab === tab ? 'text-white' : 'text-primaryBlue'
                } text-sm font-medium`}
                onClick={() => {
                  setSelectedTab(tab);
                  setTabPage(1);
                }}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="w-full lg:col-span-9 flex flex-col justify-start items-start gap-2 py-6 px-4 lg:px-8 h-[400px] overflow-y-auto">
            <form onSubmit={createPoolForm.handleSubmit} className="w-full">
              {selectedTab !== 'Pool Members' ? (
                <>
                  {tabConfigs[selectedTab] && (
                    <TabContent
                      title={tabConfigs[selectedTab].title}
                      inputs={tabConfigs[selectedTab].inputs}
                    />
                  )}
                </>
              ) : (
                <div className="w-full">
                  <h3 className="text-gray-800 text-xl font-semibold mb-10">
                    {tabConfigs[selectedTab]?.title}
                  </h3>
                  <div className="w-full">
                    <h3 className="text-gray-700 text-lg font-semibold mb-4">
                      Pool Members
                    </h3>
                    {createPoolForm.values.createPool === 'yes' && createPoolForm.values.pools[0].createHealthMonitor === 'yes' && (
                      <div className="mb-6 p-4 bg-blue-100 dark:bg-blue-900 rounded-md">
                        <p className="text-gray-800 dark:text-gray-200 text-sm">
                          <span className="font-semibold">Pricing:</span> Each pool member/node costs{' '}
                          {currency === "NGN" ? (
                            <span className="font-bold">₦17,500</span>
                          ) : (
                            <span className="font-bold">$11</span>
                          )} per month.
                        </p>
                        <p className="text-gray-800 dark:text-gray-200 text-sm mt-2">
                          You have added{' '}
                          <span className="font-bold">{createPoolForm.values.pools[0].members.length}</span> member(s).
                          Estimated monthly cost: {' '}
                          {currency === "NGN" ? (
                            <span className="font-bold">₦{createPoolForm.values.pools[0].members.length * 17500}</span>
                          ) : (
                            <span className="font-bold">${createPoolForm.values.pools[0].members.length * 11}</span>
                          )}.
                        </p>
                      </div>
                    )}
                    {createPoolForm.values.createPool === 'yes' && createPoolForm.values.pools[0].createHealthMonitor === 'yes' ? (
                      visibleEntries.map((member, index) => (
                        <div
                          key={index + firstEntryIndex}
                          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 border-b pb-4"
                        >
                          <Input
                            type="text"
                            label="IP Address *"
                            name={`pools[0].members[${index + firstEntryIndex}].address`}
                            placeholder="e.g., 192.0.2.51"
                            value={member.address}
                            onChange={createPoolForm.handleChange}
                            onBlur={createPoolForm.handleBlur}
                            onError={
                              createPoolForm.touched.pools?.[0]?.members?.[index + firstEntryIndex]?.address &&
                              createPoolForm.errors.pools?.[0]?.members?.[index + firstEntryIndex]?.address
                                ? createPoolForm.errors.pools[0].members[index + firstEntryIndex].address
                                : null
                            }
                          />
                          <Input
                            type="number"
                            label="Port *"
                            name={`pools[0].members[${index + firstEntryIndex}].protocol_port`}
                            placeholder="e.g., 80"
                            value={member.protocol_port}
                            onChange={createPoolForm.handleChange}
                            onBlur={createPoolForm.handleBlur}
                            onError={
                              createPoolForm.touched.pools?.[0]?.members?.[index + firstEntryIndex]?.protocol_port &&
                              createPoolForm.errors.pools?.[0]?.members?.[index + firstEntryIndex]?.protocol_port
                                ? createPoolForm.errors.pools[0].members[index + firstEntryIndex].protocol_port
                                : null
                            }
                          />
                          <Input
                            type="text"
                            label="Name *"
                            name={`pools[0].members[${index + firstEntryIndex}].name`}
                            placeholder="e.g., Member 1"
                            value={member.name}
                            onChange={createPoolForm.handleChange}
                            onBlur={createPoolForm.handleBlur}
                            onError={
                              createPoolForm.touched.pools?.[0]?.members?.[index + firstEntryIndex]?.name &&
                              createPoolForm.errors.pools?.[0]?.members?.[index + firstEntryIndex]?.name
                                ? createPoolForm.errors.pools[0].members[index + firstEntryIndex].name
                                : null
                            }
                          />
                          <Input
                            type="number"
                            label="Weight *"
                            name={`pools[0].members[${index + firstEntryIndex}].weight`}
                            placeholder="1"
                            value={member.weight}
                            onChange={createPoolForm.handleChange}
                            onBlur={createPoolForm.handleBlur}
                            onError={
                              createPoolForm.touched.pools?.[0]?.members?.[index + firstEntryIndex]?.weight &&
                              createPoolForm.errors.pools?.[0]?.members?.[index + firstEntryIndex]?.weight
                                ? createPoolForm.errors.pools[0].members[index + firstEntryIndex].weight
                                : null
                            }
                          />
                          <button
                            type="button"
                            className="text-red-500 text-sm"
                            onClick={() => removeMember(index + firstEntryIndex)}
                            disabled={createPoolForm.values.pools[0].members.length === 1}
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500">
                        Pool members are not required because pool or health monitor creation is disabled.
                      </p>
                    )}
                    {createPoolForm.values.createPool === 'yes' && createPoolForm.values.pools[0].createHealthMonitor === 'yes' && (
                      <>
                        <button
                          type="button"
                          className="py-2 px-4 rounded-md text-sm border border-primaryBlue text-primaryBlue cursor-pointer"
                          onClick={addMember}
                        >
                          Add Member
                        </button>
                        <div className="flex justify-between items-center mt-4">
                          <p className="text-gray-400 text-sm">
                            Showing{' '}
                            <span className="font-medium text-black dark:text-white">
                              {firstEntryIndex + 1}
                            </span>{' '}
                            to{' '}
                            <span className="font-medium text-black dark:text-white">
                              {Math.min(
                                lastEntryIndex,
                                createPoolForm.values.pools[0].members.length
                              )}
                            </span>{' '}
                            of{' '}
                            <span className="font-medium text-black dark:text-white">
                              {createPoolForm.values.pools[0].members.length}
                            </span>{' '}
                            members
                          </p>
                          <div className="flex gap-3">
                            <button
                              className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${
                                tabPage === 1 ? 'opacity-50' : 'opacity-100'
                              }`}
                              onClick={navigateToPrevious}
                              disabled={tabPage === 1}
                              type="button"
                            >
                              Previous
                            </button>
                            <button
                              className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${
                                tabPage === totalTabPages ? 'opacity-50' : 'opacity-100'
                              }`}
                              onClick={navigateToNext}
                              disabled={tabPage === totalTabPages}
                              type="button"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    <LoadBalancerInstances/>
                  </div>
                </div>
              )}
            </form>
          </div>

          <div className="lg:col-span-12 border-t border-gray-200 w-full flex justify-between items-center p-6">
            <button
              type="button"
              className="px-4 py-4 text-xs border border-primaryBlue rounded-sm text-primaryBlue cursor-pointer text-center"
              onClick={onClose}
            >
              Cancel
            </button>
            <div className="w-full flex justify-end items-center gap-2">
              <button
                type="button"
                className={`px-4 py-4 text-xs border border-primaryBlue rounded-sm text-primaryBlue cursor-pointer text-center ${
                  tabsBalancers.indexOf(selectedTab) === 0 && tabPage === 1 ? 'opacity-50' : 'opacity-100'
                }`}
                onClick={navigateToPrevious}
                disabled={tabsBalancers.indexOf(selectedTab) === 0 && tabPage === 1}
              >
                {'<'} Back
              </button>
              {selectedTab !== tabsBalancers[tabsBalancers.length - 1] ? (
                <button
                  type="button"
                  className={`px-4 py-4 text-xs border border-primaryBlue rounded-sm text-primaryBlue cursor-pointer text-center ${
                    tabsBalancers.indexOf(selectedTab) === tabsBalancers.length - 1 ? 'opacity-50' : 'opacity-100'
                  }`}
                  onClick={navigateToNext}
                  disabled={tabsBalancers.indexOf(selectedTab) === tabsBalancers.length - 1}
                >
                  Next {'>'}
                </button>
              ) : (
                <Button
                  buttonText="Create Pool"
                  buttonRole="submit"
                  type="submit"
                  handleClick={createPoolForm.handleSubmit}
                  disabled={!isTabValid['Pool Details *'] || !isTabValid['Monitor Details *']}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePool;